import {
  Form, FormActions, FormActionsAlign, FormProps, FormSubmitType,
} from '@doveit/bricks';
import React, { FunctionComponent, useCallback, useMemo } from 'react';
import parsePhoneNumber from 'libphonenumber-js/max';
import * as Yup from 'yup';
import { subYears } from 'date-fns';
import { formatDate } from '@doveit/hammer';
import validationSchema from './ContactForm.schema';
import { COUNTRY } from '../../../constants';
import { deepMerge } from '../../../utils/object/object';
import { GeoFormModel } from '../../../geo/components/form-geo-field/types';
import { GEO_FORM_MODEL_DEFAULT_VALUES } from '../../../geo/components/form-geo-field/constants';
import FormGeoField from '../../../geo/components/form-geo-field/FormGeoField';
import { asSelectOptions } from '../../../select-options/utils/asSelectOptions';
import { identityDocumentTypeLabels } from '../../../labels';
import { IDENTITY_DOCUMENT_ISSUED_BY_PLACEHOLDER, IDENTITY_DOCUMENT_TYPE_PLACEHOLDER } from '../../../property/assignment/components/assignment-wizard/assignment-wizard-planimetry-delegation/AssignmentWizardPlanimetryDelegationForm';
import { formatInputDate } from '../../../utils/form';
import { IdentityDocumentType } from '../../../domain/types';

export interface ContactFormModel {
  name?: string,
  phoneNumber?: string,
  email?: string,
  fiscalCode?: string,
  address?: GeoFormModel,
  notes?: string,
  identityDocument?: {
    type?: string,
    number?: string,
    issuedBy?: string,
    issuedAt?: string,
  },
  birthDate?: string,
  birthPlace?: string,
  citizenship?: string
}

export interface ContactFormProps {
  onSubmit: FormSubmitType<ContactFormModel>,
  initialValues?: ContactFormModel,
  oldInitialValues?: Partial<ContactFormModel>,
  loading?: boolean,
  submitLabel?: string,
  innerRef?: FormProps<ContactFormModel>['innerRef'],
  requiredFields?: Partial<Record<keyof ContactFormModel, boolean>>,
  schema?: Yup.AnySchema,
  showIdentityDocument?: boolean,
  showBirthData?: boolean,
  showCitizenship?: boolean,
}

export const CONTACT_FORM_DEFAULT_VALUES: ContactFormModel = {
  name: '',
  phoneNumber: '',
  email: '',
  fiscalCode: '',
  address: GEO_FORM_MODEL_DEFAULT_VALUES,
  notes: '',
  identityDocument: {
    type: '',
    number: '',
    issuedBy: '',
    issuedAt: '',
  },
  birthDate: '',
  birthPlace: '',
  citizenship: '',
};

const ContactForm: FunctionComponent<ContactFormProps> = ({
  initialValues = {},
  oldInitialValues = {},
  loading,
  onSubmit,
  submitLabel = 'Salva',
  innerRef,
  schema = validationSchema,
  requiredFields,
  showIdentityDocument = false,
  showBirthData = false,
  showCitizenship = false,
}) => {
  const minIdentityDocumentIssuedAt = React.useMemo(() => formatInputDate(subYears(new Date(), 10)), []);
  const today = React.useMemo(() => formatInputDate(new Date()), []);

  const mergedInitialValues = useMemo<ContactFormModel>(() => deepMerge(
    CONTACT_FORM_DEFAULT_VALUES,
    initialValues,
  ), [initialValues]);

  const onInternalSubmit = useCallback((values: ContactFormModel, actions: FormActions<ContactFormModel>) => {
    let { phoneNumber } = values;

    if (values.phoneNumber) {
      const parsed = parsePhoneNumber(values.phoneNumber, COUNTRY);
      phoneNumber = parsed?.number.toString();
    }

    onSubmit({
      ...values,
      phoneNumber,
    }, actions);
  }, [onSubmit]);

  return (
    <Form
      initialValues={mergedInitialValues}
      onSubmit={onInternalSubmit}
      validationSchema={schema}
      loading={loading}
      innerRef={innerRef}
      enableReinitialize
      preventEnterKeypress
    >
      {({ values }) => (
        <>
          <Form.Item>
            <Form.Input
              name="name"
              aria-label="Nome e cognome"
              label="Nome e cognome"
              placeholder="Es. Mario Rossi"
              required={requiredFields?.name}
              oldValue={oldInitialValues.name !== values.name ? oldInitialValues.name : undefined}
            />
          </Form.Item>
          <Form.Item size={{ MD: 1 / 2 }}>
            <Form.Input
              name="phoneNumber"
              aria-label="Telefono"
              label="Telefono"
              placeholder="Es. 3923456789"
              required={requiredFields?.phoneNumber}
              oldValue={oldInitialValues.phoneNumber !== values.phoneNumber ? oldInitialValues.phoneNumber : undefined}
            />
          </Form.Item>
          <Form.Item size={{ MD: 1 / 2 }}>
            <Form.Input
              name="email"
              aria-label="Email"
              label="Email"
              placeholder="Es. mario.rossi@gmail.com"
              required={requiredFields?.email}
              oldValue={oldInitialValues.email !== values.email ? oldInitialValues.email : undefined}
            />
          </Form.Item>
          <Form.Item size={{ MD: 1 / 2 }}>
            <Form.Input
              name="fiscalCode"
              aria-label="Codice fiscale"
              label="Codice fiscale"
              placeholder="Es. MRLMRL80A01F839I"
              required={requiredFields?.fiscalCode}
              oldValue={oldInitialValues.fiscalCode !== values.fiscalCode ? oldInitialValues.fiscalCode : undefined}
            />
          </Form.Item>
          {showCitizenship && (
            <Form.Item size={{ MD: 1 / 2 }}>
              <Form.Input
                name="citizenship"
                aria-label="Cittadinanza"
                label="Cittadinanza"
                placeholder="Es. Italiana"
                required={requiredFields?.citizenship}
                oldValue={oldInitialValues.citizenship !== values.citizenship ? oldInitialValues.citizenship : undefined}
              />
            </Form.Item>
          )}
          {showBirthData && (
            <>
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Input
                  name="birthDate"
                  type="date"
                  label="Data di nascita"
                  aria-label="Campo per inserire la data di nascita"
                  required={requiredFields?.birthDate}
                  oldValue={
                    oldInitialValues.birthDate && oldInitialValues.birthDate !== values.birthDate
                      ? formatDate(new Date(oldInitialValues.birthDate))
                      : undefined
                  }
                  max={today}
                />
              </Form.Item>
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Input
                  name="birthPlace"
                  label="Luogo di nascita"
                  aria-label="Campo per inserire il luogo di nascita"
                  required={requiredFields?.birthPlace}
                  oldValue={
                    oldInitialValues.birthPlace !== values.birthPlace
                      ? oldInitialValues.birthPlace
                      : undefined
                  }
                  placeholder="Es. Milano"
                />
              </Form.Item>
            </>
          )}
          <Form.Item>
            <FormGeoField
              name="address"
              required={requiredFields?.address}
              enableManualEntry
              oldValue={oldInitialValues.address && (oldInitialValues.address?.normalizedAddress?.description !== values.address?.normalizedAddress?.description)
                ? oldInitialValues.address?.normalizedAddress?.description
                : undefined}
            />
          </Form.Item>
          <Form.Item>
            <Form.TextArea
              name="notes"
              aria-label="Note"
              label="Note"
              placeholder="Inserisci eventuali note sul contatto"
              required={requiredFields?.notes}
            />
          </Form.Item>
          {showIdentityDocument && (
            <Form.Fieldset legend="Documento di riconoscimento">
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Select
                  name="identityDocument.type"
                  label="Tipo di documento"
                  aria-label="Campo per selezionare la tipologia di documento di riconoscimento"
                  options={asSelectOptions(identityDocumentTypeLabels)}
                  oldValue={oldInitialValues.identityDocument?.type !== values.identityDocument?.type ? identityDocumentTypeLabels[oldInitialValues.identityDocument?.type as IdentityDocumentType] : undefined}
                />
              </Form.Item>
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Input
                  name="identityDocument.number"
                  label="Numero"
                  aria-label="Campo per inserire il numero del documento di riconoscimento"
                  oldValue={oldInitialValues.identityDocument?.number !== values.identityDocument?.number ? oldInitialValues.identityDocument?.number : undefined}
                  placeholder={values.identityDocument?.type ? IDENTITY_DOCUMENT_TYPE_PLACEHOLDER[values.identityDocument.type as IdentityDocumentType] : 'Es. XXXXXXXXX'}
                />
              </Form.Item>
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Input
                  name="identityDocument.issuedBy"
                  label="Rilasciato da"
                  aria-label="Campo per inserire l'ente di emissione del documento di riconoscimento"
                  oldValue={
                    oldInitialValues.identityDocument?.issuedBy !== values.identityDocument?.issuedBy
                      ? oldInitialValues.identityDocument?.issuedBy
                      : undefined
                  }
                  placeholder={
                    values.identityDocument?.issuedBy && values.identityDocument?.type
                      ? IDENTITY_DOCUMENT_ISSUED_BY_PLACEHOLDER[values.identityDocument.type as IdentityDocumentType]
                      : ''
                  }
                />
              </Form.Item>
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Input
                  name="identityDocument.issuedAt"
                  type="date"
                  label="Data rilascio"
                  aria-label="Campo per inserire la data di rilascio del documento di riconoscimento"
                  oldValue={
                    oldInitialValues.identityDocument?.issuedAt && oldInitialValues.identityDocument?.issuedAt !== values.identityDocument?.issuedAt
                      ? formatDate(new Date(oldInitialValues.identityDocument.issuedAt))
                      : undefined
                  }
                  min={minIdentityDocumentIssuedAt}
                  max={today}
                />
              </Form.Item>
            </Form.Fieldset>
          )}
          {!innerRef && (
            <Form.Actions align={FormActionsAlign.RIGHT}>
              <Form.Submit label={submitLabel} />
            </Form.Actions>
          )}
        </>
      )}
    </Form>
  );
};

export default ContactForm;
